// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

/**
 * Clean text for TTS synthesis.
 *
 * - Cartesia: converts [[emotion:...]] / [[speed:...]] to XML tags
 * - Hume: strips all voice tags (Hume auto-detects emotion)
 * - Both: strip markdown formatting
 */
export function stripForTTS(text, provider) {
  let clean = text;
  if (provider === 'cartesia') {
    clean = clean
      // [[emotion:calm]] or [[emotion,calm]] → XML tag
      .replace(/\[\[emotion[:,]([^\]]+)\]\]/g, '<emotion value="$1"/>')
      // [[speed:0.95]] or [[speed,0.95]] → XML tag
      .replace(/\[\[speed[:,]([^\]]+)\]\]/g, '<speed ratio="$1"/>')
      // [[calm]] [[laughter]] etc → treat as Cartesia tag
      .replace(/\[\[([a-z_]+)\]\]/gi, (_, tag) =>
        tag.toLowerCase() === 'laughter' ? '[laughter]' : `<emotion value="${tag}"/>`)
      // {{anything}} → strip
      .replace(/\{\{[^}]*\}\}/g, '');
    // Cartesia needs markdown stripped and punctuation normalized
    clean = clean
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1').replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1').replace(/__(.+?)__/g, '$1')
      .replace(/_(.+?)_/g, '$1').replace(/~~(.+?)~~/g, '$1')
      .replace(/`{1,3}[^`]*`{1,3}/g, '').replace(/^#{1,6}\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '').replace(/^\s*\d+\.\s+/gm, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/!\[[^\]]*\]\([^)]+\)/g, '')
      .replace(/^>\s*/gm, '').replace(/---+|===+|\*\*\*+/g, '')
      .replace(/\.{2,}/g, ',')  // ... or .. → comma (natural pause for TTS)
      .replace(/:/g, ',').replace(/—/g, ', ').replace(/–/g, ', ')
      .replace(/\n{2,}/g, '. ').replace(/\s{2,}/g, ' ').trim();
  } else {
    // Hume understands text naturally — just strip all voice tags (preserve spacing)
    clean = clean
      .replace(/\[\[(?:emotion|speed)[:,][^\]]*\]\]/g, '')
      .replace(/\[\[([a-z_]+)\]\]/gi, '')
      .replace(/\{\{[^}]*\}\}/g, '')
      .replace(/<\/?(?:emotion|speed|volume)(?:\s+(?:value|ratio)="[^"]*")?\s*\/?>/g, '')
      .replace(/\n{2,}/g, '. ');
  }
  return clean;
}

/** Strip voice tags from display text (used as chatEvents text transform). */
export function stripVoiceTags(text) {
  return text
    .replace(/\[\[(?:emotion|speed)[:,][^\]]*\]\]/g, '')
    .replace(/\[\[([a-z_]+)\]\]/gi, '')
    .replace(/\{\{[^}]*\}\}/g, '')
    .replace(/<\/?(?:emotion|speed|volume)(?:\s+(?:value|ratio)="[^"]*")?\s*\/?>/g, '');
}
